import mergeByProp from '../../lib/mergeByProp';
import createPersistedReducer from '../lib/createPersistedReducer';
import { generateDeepDiffs } from '@shared/utils/generateDeepDiffs.js';
import { randomId } from '@shared/utils/randomId.js';
import applyChangesToStep from '../../lib/applyChangesToStep';
import { createTransform } from 'redux-persist';
import fastMergeByProp from '@/lib/fastMergeByProp.js';

const getUniqueKey = (change) => {
  const entries = [change?.type];
  const operation = change?.operation || 'update';
  ['appId', 'appVersion', 'workflowId', 'stepId', 'credentialId', 'platformSecretId', 'companyId', 'ids'].forEach((key) => {
    if (change[key] !== undefined) {
      const string = Array.isArray(change[key]) ? change[key].join('-') : change[key];
      entries.push(string);
    }
  });
  if (change?.path) {
    entries.push(change?.path);
  }
  entries.push(operation);
  return entries.join('_');
};

const initialState = {
  isSyncing: false,
  isUpdating: false,
  updateState: {},
  changesQueue: [], // changes made by the current user to be synced
  updateQueue: [], // changes made by other users or when there was a conflict in changesQueue
  triggerMutationsQueue: [], // changes made by the current user on the workflow trigger panel
  syncFailed: false,
  lastSyncResult: null, // durable, target-aware outcome of the last sync batch: { ok, at, stepIds }
  appsMetadata: [],
  appsVersions: [],
  stepState: {},
  stepAppVersions: {},
  activeInputState: undefined,
  selectedSteps: [],
  workflows: [],
  workflowsMetadata: [],
  cirraWorkflows: [],
  publishedWorkflows: [],
  allPublishedWorkflows: [],
  workflowLogs: [],
  workflowRunDetailsLogs: [],
  workflowLogsMetadata: [],
  workflowLogOutput: {},
  workflowLogOperationLogs: {},
  stepOutput: {},
  credentials: [],
  searchCredentials: [],
  credentialsByCompany: {},
  workflowComments: {},
  workflowLookups: {},
  appActionsUsage: {},
  appActionOutputSummary: {},
  appAuthUsage: {},
  deploymentProgress: {},
  appActionLookups: [],
  appActionOutputs: [],
  appActionOutboundOutputs: [],
  activeCanvasStepAddButtonRelationships: {},
  workflowOperationLogs: [],
  workflowOperationLogOutput: {},
  workflowRunUpdate: {},
  workflowStepTriggerData: {},
  workflowDiffLastChange: randomId(),
  workflowUndoStack: [],
  workflowRedoStack: [],
  workflowSnapshots: [],
  workflowAutosaves: [],
  workflowListLogs: [],
  userWorkflowAlerts: [],
  actionSuggestions: { error: false },
  workflowTags: [],
  recipes: [],
  recentlyUsedApps: [],
  nextStepsState: [],
  dependentStepIds: [],
  webhookResponses: {},
  longPollResponses: {},
  tutorialScripts: {},
  tutorialEvent: null,
  activeStatusId: null,
  currentTutorialRequirement: null,
  lookupReloadQueue: [],
  netsuiteRecordSchema: {},
  netsuiteRecordSchemaError: {},
  graphqlSchema: {},
  slackInstallation: null,
  slackInstallationChannels: [],
  auditLogs: { data: [], meta: {} },
  netsuiteCache: {}
};

const persistConfig = {
  key: 'workflowReducer',
  whitelist: ['appsMetadata', 'changesQueue', 'recentlyUsedApps', 'graphqlSchema'],
  transforms: [
    createTransform(
      (state, key) => {
        const newState = { ...state };
        if (key === 'recentlyUsedApps') {
          const THIRTY_MINUTES_MS = 30 * 60 * 1000;
          const currentTime = new Date().getTime();

          // Filter out entries older than 30 minutes
          const filteredApps = {};
          Object.entries(newState).forEach(([appId, timestamp]) => {
            if (currentTime - timestamp < THIRTY_MINUTES_MS) {
              filteredApps[appId] = timestamp;
            }
          });
          return filteredApps;
        }
        if (key === 'graphqlSchema') {
          const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
          const currentTime = new Date().getTime();

          const filteredSchemas = {};
          Object.entries(newState).forEach(([schemaKey, schemaData]) => {
            if (schemaData?.timestamp && currentTime - schemaData.timestamp < SEVEN_DAYS_MS) {
              filteredSchemas[schemaKey] = schemaData;
            }
          });
          return filteredSchemas;
        }
        return newState;
      },
      (state) => state,
      { whitelist: ['recentlyUsedApps', 'graphqlSchema'] }
    )
  ]
};

export default createPersistedReducer(
  initialState,
  {
    GET_COMPANY_WORKFLOWS_SUCCESS: (state, payload) => ({
      ...state,
      workflows: mergeByProp(['id', 'version'], state.workflows, payload.data, (oldWorkflow, newWorkflow) => {
        return {
          ...newWorkflow,
          variables: {
            ...(newWorkflow?.variables || {}),
            ...(oldWorkflow?.variables || {})
          }
        };
      })
    }),

    GET_ALL_PUBLISHED_WORKFLOWS_SUCCESS: (state, payload) => ({
      ...state,
      allPublishedWorkflows: mergeByProp('id', state.allPublishedWorkflows, payload.data)
    }),

    GET_WORKFLOW_SUCCESS: (state, payload) => ({
      ...state,
      workflows: payload.meta.isPublished ? state.workflows : mergeByProp(['id', 'version'], state.workflows, payload.data),
      publishedWorkflows: payload.meta.isPublished ? mergeByProp(['id'], state.publishedWorkflows, payload.data) : state.publishedWorkflows
    }),

    GET_WORKFLOW_STATE_SUCCESS: (state, payload) => ({
      ...state,
      workflows: mergeByProp(
        ['id', 'version'],
        state.workflows,
        payload.data?.activeEditorWorkflows?.filter((n) => !n?.publishedOn)
      ),
      publishedWorkflows: mergeByProp(
        ['id', 'version'],
        state.workflows,
        payload.data?.activeEditorWorkflows?.filter((n) => !!n?.publishedOn)
      )
      // appsVersions: mergeByProp(['id', 'version'], [...state.appsVersions], [...payload.data?.apps])
    }),

    GET_WORKFLOW_STEP_TRIGGER_DATA_SUCCESS: (state, payload) => ({
      ...state,
      workflowStepTriggerData: {
        ...state.workflowStepTriggerData,
        [payload?.data?.stepId]: payload.data
      }
    }),

    GET_WORKFLOWS_SUCCESS: (state, payload) => {
      if (state.changesQueue.length > 0) {
        const workflows = [...state.workflows];
        const toApply = [];
        const toUpdate = [];

        payload.data.forEach((workflow) => {
          const current = workflows.find((n) => n.id === workflow.id && n.version === 'draft');
          const diffs = current ? generateDeepDiffs(workflow, current) || [] : [];
          if (diffs.length > 0) {
            toUpdate.push({ type: 'workflow', workflowId: workflow.id });
          } else {
            toApply.push(workflow);
          }
        });

        return {
          ...state,
          updateQueue: mergeByProp('workflowId', [...state.updateQueue], toUpdate),
          workflows: mergeByProp(['id', 'version'], [...state.workflows], toApply)
        };
      }

      return {
        ...state,
        workflows: mergeByProp(['id', 'version'], state.workflows, payload.data)
      };
    },
    GET_WORKFLOW_STEPS_SUCCESS: (state, payload) => {
      if (state.changesQueue.length > 0 && !payload?.meta?.forceUpdate) {
        const toUpdate = [];
        const toApply = [];

        payload.data.forEach((step) => {
          const currentWorkflow = [...state.workflows].find((el) => el.id === step.workflowId && el.version === 'draft');
          const currentStep = currentWorkflow?.steps?.find((el) => el.id === step.id);
          const diffs = currentStep ? generateDeepDiffs(step, currentStep) || [] : [];

          diffs.length > 0 ? toUpdate.push({ type: 'workflowStep', stepId: step.id }) : toApply.push(step);
        });

        return {
          ...state,
          updateQueue: mergeByProp('stepId', [...state.updateQueue], toUpdate),
          workflows: [...state.workflows].map((workflow) => {
            const workflowSteps = toUpdate.filter((step) => step.workflowId === workflow.id && workflow.version === 'draft');
            if (workflowSteps.length > 0) {
              return { ...workflow, steps: mergeByProp('id', workflow.steps, workflowSteps) };
            }
            return workflow;
          })
        };
      }

      return {
        ...state,
        workflows: [...state.workflows].map((workflow) => {
          const workflowSteps = payload.data.filter((step) => step?.workflowId === workflow?.id && workflow?.version === 'draft');
          return workflowSteps.length > 0 ? { ...workflow, steps: mergeByProp('id', workflow.steps, workflowSteps) } : workflow;
        })
      };
    },

    PUT_WORKFLOW: (state, payload) => {
      return {
        ...state,
        workflows: mergeByProp(['id', 'version'], state.workflows, [payload])
      };
    },

    PUT_WORKFLOW_SUCCESS: (state, payload) => {
      return {
        ...state,
        workflows: mergeByProp(['id', 'version'], state.workflows, payload.data)
      };
    },

    PUT_WORKFLOW_METADATA: (state, payload) => {
      return {
        ...state,
        workflowsMetadata: mergeByProp(['id'], state.workflowsMetadata, [payload], (existing, incoming) => ({
          ...existing,
          ...incoming
        }))
      };
    },

    POST_WORKFLOW: (state, payload) => ({
      ...state,
      workflows: [...state.workflows, { ...(payload.workflow || {}) }],
      workflowsMetadata: [...state.workflowsMetadata, { id: payload.id, ...(payload.meta || {}) }]
    }),

    POST_WORKFLOW_SUCCESS: (state, payload) => ({
      ...state,
      workflows: [...state.workflows, ...payload.data]
    }),

    DELETE_WORKFLOW: (state, payload) => ({
      ...state,
      workflows: state.workflows.filter((n) => n.id !== payload.id),
      workflowsMetadata: state.workflowsMetadata.filter((n) => n.id !== payload.id)
    }),

    DELETE_WORKFLOW_SUCCESS: (state, payload) => ({
      ...state,
      workflows: state.workflows.filter((n) => n.id !== payload.meta.id),
      workflowsMetadata: state.workflowsMetadata.filter((n) => n.id !== payload.meta.id)
    }),

    DELETE_WORKFLOW_LOG_SUCCESS: (state, payload) => ({
      ...state,
      workflowLogs: state.workflowLogs.filter((n) => n.id !== payload.meta.workflowLogId)
    }),

    DELETE_APP: (state, payload) => ({
      ...state,
      appsVersions: state.appsVersions.filter((n) => n.id !== payload.id),
      appsMetadata: state.appsMetadata.filter((n) => n.id !== payload.id)
    }),

    DELETE_APP_SUCCESS: (state, payload) => ({
      ...state,
      appsVersions: state.appsVersions.filter((n) => n.id !== payload.meta.id),
      appsMetadata: state.appsMetadata.filter((n) => n.id !== payload.meta.id)
    }),

    POST_RUN_APP_ACTION_LOOKUP_SUCCESS: (state, payload) => ({
      ...state,
      appActionLookups: mergeByProp('actionId', state.appActionLookups || [], [
        {
          actionId: payload?.meta?.actionId,
          appId: payload?.meta?.appId,
          data: payload?.data
        }
      ])
    }),

    SET_WORKFLOW_ACTIVE_INPUT_STATE: (state, payload) => ({
      ...state,
      activeInputState: {
        ...state.activeInputState,
        ...payload
      }
    }),

    POST_RUN_WORKFLOW_STEP_SUCCESS: (state, payload) => ({
      ...state,
      workflows: state?.workflows?.map((n) =>
        n?.id !== payload?.meta?.workflowId || !payload?.meta?.step
          ? n
          : {
              ...n,
              steps: mergeByProp('id', n?.steps, [payload.meta.step]),
              variables: {
                ...n?.variables,
                memory: payload.meta.variables.memory
              }
            }
      ),
      stepOutput: {
        ...state.stepOutput,
        [payload?.meta?.stepId]: payload
      }
    }),

    POST_RUN_MULTIPLE_WORKFLOW_STEPS_SUCCESS: (state, payload) => ({
      ...state,
      workflows: state?.workflows?.map((n) =>
        n?.id !== payload?.meta?.workflowId
          ? n
          : {
              ...n,
              steps: mergeByProp('id', n?.steps, payload?.steps),
              variables: {
                ...n?.variables,
                memory: payload?.variables?.memory
              }
            }
      ),
      stepOutput: {
        ...state.stepOutput,
        ...(payload?.stepOutputs || {})
      }
    }),

    DELETE_WORKFLOW_STEP_OUTPUT: (state, payload) => ({
      ...state,
      stepOutput: {
        ...state.stepOutput,
        [payload?.stepId]: []
      }
    }),

    SET_WORKFLOW_SELECTED_STEPS: (state, payload) => ({
      ...state,
      selectedSteps: payload
    }),

    GET_CREDENTIALS_SUCCESS: (state, payload) => ({
      ...state,
      credentials: mergeByProp(['credentialId'], [...state.credentials], payload?.data || [])
    }),
    GET_CREDENTIALS_BY_COMPANY_SUCCESS: (state, payload) => ({
      ...state,
      credentialsByCompany: {
        ...state.credentialsByCompany,
        [payload?.meta?.companyId]: payload?.data || []
      }
    }),
    GET_ORG_STATE_SUCCESS: (state, payload) => ({
      ...state,
      credentials: mergeByProp(['credentialId'], [...state.credentials], payload?.data?.credentials || [], (old, nw) => {
        const secrets = { ...(old?.secrets || {}), ...(nw?.secrets || {}) };
        return { ...old, ...nw, secrets: Object.keys(secrets).length > 0 ? secrets : undefined };
      }),
      workflowsMetadata: payload?.data?.workflowsMetadata,
      workflowTags: payload?.data?.workflowTags
    }),

    GET_WORKFLOWS_METADATA_SUCCESS: (state, payload) => ({
      ...state,
      workflowsMetadata: payload?.data
    }),

    GET_CIRRA_WORKFLOWS_SUCCESS: (state, payload) => ({
      ...state,
      cirraWorkflows: Array.isArray(payload?.data) ? payload.data : []
    }),

    PUT_CREDENTIAL: (state, payload) => {
      const appId = payload.appId;
      const companyId = payload.companyId;
      const isSettingDefault = payload.isDefault;
      const resetDefaultCredentials = state?.credentials
        ?.filter((n) => n.appId === appId && n.companyId === companyId && n.isDefault) //
        .map((n) => ({ ...n, isDefault: isSettingDefault ? false : n.isDefault }));

      const mergeCallback = (orig, nw) => ({
        ...orig,
        ...nw,
        meta: {
          ...orig?.meta,
          ...nw?.meta,
          label: nw?.secrets?.label || nw?.meta?.label || orig?.meta?.label
        },
        secrets: {
          ...orig?.secrets,
          ...nw?.secrets
        }
      });

      const nextCredentials = mergeByProp(['credentialId'], [...state.credentials], [payload, ...(resetDefaultCredentials || [])], mergeCallback);

      return {
        ...state,
        credentials: nextCredentials,
        credentialsByCompany: {
          ...state.credentialsByCompany,
          [companyId]: mergeByProp(['credentialId'], [...(state.credentialsByCompany[companyId] || [])], [payload, ...(resetDefaultCredentials || [])], mergeCallback)
        }
      };
    },

    DELETE_CREDENTIAL: (state, payload) => ({
      ...state,
      credentials: state?.credentials?.filter((n) => n?.credentialId !== payload?.credentialId) || [],
      credentialsByCompany: {
        ...state.credentialsByCompany,
        [payload.companyId]: (state.credentialsByCompany[payload.companyId] || []).filter((n) => n?.credentialId !== payload?.credentialId)
      }
    }),

    PUT_CREDENTIAL_SUCCESS: (state, payload) => ({
      ...state,
      credentials: mergeByProp('id', [...state.credentials], [...payload.data])
    }),

    DELETE_CREDENTIAL_SUCCESS: (state, payload) => ({
      ...state,
      credentials: state?.credentials?.filter((n) => n?.credentialId !== payload?.meta?.credentialId) || []
    }),

    GET_CREDENTIAL_BY_ID_SUCCESS: (state, payload) => {
      if (state.changesQueue.length > 0 && !payload?.meta?.forceUpdate) {
        const credentials = [...state.credentials];
        const toApply = [];
        const toUpdate = [];

        payload.data.forEach((credential) => {
          const current = credentials.find((n) => n.credentialId === credential.credentialId);
          const diffs = current ? generateDeepDiffs(credential, current) || [] : [];
          if (diffs.length > 0) {
            toUpdate.push({ type: 'credential', credentialId: credential.credentialId });
          } else {
            toApply.push(credential);
          }
        });

        return {
          ...state,
          updateQueue: mergeByProp('credentialId', [...state.updateQueue], toUpdate),
          credentials: state.credentials.map((oldCred) => {
            const newCred = toApply.find((n) => n?.credentialId === oldCred?.credentialId);
            if (!newCred) return oldCred;
            return {
              ...oldCred,
              ...newCred,
              meta: { ...oldCred?.meta, ...newCred?.meta },
              secrets: { ...oldCred?.secrets, ...newCred?.secrets }
            };
          })
        };
      }

      return {
        ...state,
        credentials: mergeByProp(['credentialId'], [...state.credentials], payload?.data || [], (old, nw) => {
          return { ...old, ...nw, secrets: { ...old?.secrets, ...nw?.secrets }, meta: { ...old?.meta, ...nw?.meta } };
        })
      };
    },

    POST_TEST_WORKFLOW_STEP_CREDENTIAL_SUCCESS: (state, payload) => {
      if (payload?.data?.success) {
        return {
          ...state,
          credentials: mergeByProp(['credentialId'], [...state.credentials], [payload?.data?.data])
        };
      }

      return state;
    },

    CLEAR_CREDENTIALS: (state) => {
      return {
        ...state,
        credentials: (state?.credentials || []).filter((n) => n?.companyId === 'MINDCLOUD')
      };
    },

    GET_APPS_SUCCESS: (state, payload) => {
      if (state.changesQueue.length > 0) {
        const appsVersions = state.appsVersions;
        const appsToUpdate = [];
        const appsToApply = [];

        payload.data.forEach((el) => {
          const current = appsVersions.find((n) => n.id === el.id && n.version === el.version);
          const diffs = current ? generateDeepDiffs(el, current) || [] : [];

          if (diffs.length > 0) {
            appsToUpdate.push({ type: el.category, appId: el.id, appVersion: el.version });
          } else {
            appsToApply.push(el);
          }
        });

        return {
          ...state,
          updateQueue: mergeByProp(['appId', 'appVersion'], [...state.updateQueue], appsToUpdate),
          appsVersions: mergeByProp(['id', 'version'], [...state.appsVersions], appsToApply)
        };
      }

      return {
        ...state,
        appsVersions: mergeByProp(['id', 'version'], [...state.appsVersions], [...payload.data])
      };
    },

    GET_APP_SUCCESS: (state, payload) => ({
      ...state,
      appsVersions: mergeByProp(['id', 'version'], [...state.appsVersions], [...payload.data])
    }),

    GET_APP_VERSIONS_SUCCESS: (state, payload) => ({
      ...state,
      stepAppVersions: {
        ...state.stepAppVersions,
        [payload.meta.appId]: payload.data
      }
    }),

    GET_APP_ACTIONS_USAGE_SUCCESS: (state, payload) => ({
      ...state,
      appActionsUsage: payload?.data
    }),

    GET_APP_ACTION_OUTPUT_SUMMARY_SUCCESS: (state, payload) => ({
      ...state,
      appActionOutputSummary: payload?.data
    }),

    GET_APP_AUTH_USAGE_SUCCESS: (state, payload) => ({
      ...state,
      appAuthUsage: payload?.data
    }),

    GET_APPS_METADATA_SUCCESS: (state, payload) => ({
      ...state,
      appsMetadata: payload?.data
    }),

    GET_WORKFLOW_BLUEPRINT_SHARED_APPS_SUCCESS: (state, payload) => ({
      ...state,
      appsMetadata: payload?.data
    }),

    POST_APP_SUCCESS: (state, payload) => ({
      ...state,
      appsMetadata: payload?.data
    }),

    PUT_APP: (state, newApp) => ({
      ...state,
      appsVersions: mergeByProp(['id', 'version'], [...state.appsVersions], [newApp])
    }),

    PUT_APP_AND_QUEUE_ITEM: (state, payload) => ({
      ...state,
      appsVersions: payload?.app ? fastMergeByProp(['id', 'version'], [...state.appsVersions], [payload.app]) : state.appsVersions,
      changesQueue: payload?.queueItem ? fastMergeByProp('uniqueKey', state.changesQueue, [{ ...payload.queueItem, uniqueKey: getUniqueKey(payload.queueItem) }]) : state.changesQueue
    }),

    GET_APP_MOCK_RESPONSE_SUCCESS: (state, payload) => ({
      ...state,
      stepOutput: {
        ...state.stepOutput,
        [payload?.meta?.stepId]: payload?.data?.[0]
      }
    }),

    GET_STEP_ACTION_RESPONSES_SUCCESS: (state, payload) => {
      // stepOutputId allows us to override the stepId when it is an iterate step
      const cookedStepId = payload?.meta?.stepOutputId || payload?.meta?.stepId;
      return {
        ...state,
        stepOutput: {
          ...state.stepOutput,
          [cookedStepId]: payload?.data?.[0]
        }
      };
    },

    DELETE_APP_RESPONSE_SUCCESS: (state, payload) => ({
      ...state,
      stepOutput: {
        ...state.stepOutput,
        [payload?.meta?.stepId]: []
      }
    }),

    POST_WORKFLOW_STEP_SUCCESS: (state, payload) => ({
      ...state,
      workflows: state?.workflows?.map((n) =>
        n?.id !== payload?.meta?.workflowId
          ? n
          : {
              ...n,
              graph: payload?.data?.find((n) => n.type === 'graph')?.data,
              steps: mergeByProp(
                'id',
                n?.steps,
                [payload?.data?.find((n) => n.type === 'step')?.data].filter((n) => !!n)
              )
            }
      )
    }),

    /**
     * New Sync Engine method to optimistically update the step.
     */
    PUT_WORKFLOW_STEP: (state, payload) => {
      const workflowId = payload?.workflowId;
      return {
        ...state,
        workflows: mergeByProp(['id', 'version'], state.workflows, [{ id: workflowId, version: payload?.version || 'draft' }], (workflow) => ({
          ...workflow,
          steps: mergeByProp('id', workflow?.steps, [payload])
        }))
      };
    },

    GET_PUB_SUB_MESSAGE_stepActionUpdate: (state, payload) => {
      return {
        ...state,
        stepState: { ...state.stepState, ...payload?.data }
      };
    },

    GET_PUB_SUB_MESSAGE_workflowStepUpdate: (state, payload) => {
      return {
        ...state,
        workflows: mergeByProp(['id', 'version'], state.workflows, [{ id: payload?.meta?.workflowId }], (workflow) => ({
          ...workflow,
          steps: mergeByProp('id', workflow?.steps, [...payload?.data], (orig, nw) => {
            return applyChangesToStep({ step: orig, changeObject: nw?.meta?.changes, valueKey: 'newValue' });
          })
        }))
      };
    },

    GET_PUB_SUB_MESSAGE_stepOutputUpdate: (state, payload) => ({
      ...state,
      updateState: {
        ...state?.updateState,
        stepOutputUpdate: { ...state?.updateState?.stepOutputUpdate, [payload?.workflowId]: payload }
      }
    }),

    GET_PUB_SUB_MESSAGE_deploymentProgress: (state, payload) => {
      const { realmId, step, totalSteps, message, isError, isFinish } = payload?.data || {};
      if (!realmId) return state;

      const isDeploying = step > 0 && step <= totalSteps && !isError;
      const isCompleted = step === totalSteps && !isError && isFinish;

      const newProgress = {
        step,
        totalSteps,
        message,
        isDeploying: isDeploying && !isCompleted,
        isError: !!isError,
        isCompleted
      };

      return {
        ...state,
        deploymentProgress: {
          ...state.deploymentProgress,
          [realmId]: newProgress
        }
      };
    },

    SET_DEPLOYMENT_PROGRESS: (state, payload) => {
      const { realmId, progress } = payload || {};
      if (!realmId) return state;

      return {
        ...state,
        deploymentProgress: {
          ...state.deploymentProgress,
          [realmId]: progress
        }
      };
    },

    CLEAR_DEPLOYMENT_PROGRESS: (state, payload) => {
      const { realmId } = payload || {};
      if (!realmId) return state;

      const newDeploymentProgress = { ...state.deploymentProgress };
      delete newDeploymentProgress[realmId];

      return {
        ...state,
        deploymentProgress: newDeploymentProgress
      };
    },

    GET_PUB_SUB_MESSAGE_webhookResponseUpdate: (state, payload) => ({
      ...state,
      updateState: {
        ...state?.updateState,
        webhookResponseUpdate: { ...state?.updateState?.webhookResponseUpdate, [payload?.workflowId]: payload }
      }
    }),

    WORKFLOW_UPDATE_STATE: (state, payload) => ({
      ...state,
      updateState: {
        ...state?.updateState,
        [payload?.key]: payload?.value
      }
    }),

    UNDO_WORKFLOW_ACTION: (state) => ({
      ...state,
      workflowUndoStack: state?.workflowUndoStack?.slice(1) || [],
      workflowRedoStack: [state?.workflowUndoStack?.[0], ...(state?.workflowRedoStack || [])]
    }),

    REDO_WORKFLOW_ACTION: (state) => ({
      ...state,
      workflowRedoStack: state?.workflowRedoStack?.slice(1) || [],
      workflowUndoStack: [state?.workflowRedoStack?.[0], ...(state?.workflowUndoStack || [])]
    }),

    SET_WORKFLOW_UNDO_REDRAW: (state, payload) => ({
      // TODO This fell out, need to reimplement. this should cause lexicalinput to redraw.
      ...state,
      workflowDiffLastChange: payload
    }),

    PATCH_WORKFLOW_STEPS_SUCCESS: (state, payload) => ({
      ...state,
      workflows: state?.workflows?.map((n) =>
        n?.id !== payload?.meta?.workflowId
          ? n
          : {
              ...n,
              steps: mergeByProp('id', n?.steps, [...payload.data])
            }
      )
    }),

    DELETE_WORKFLOW_STEPS_SUCCESS: (state, payload) => ({
      ...state,
      workflows: state?.workflows?.map((n) =>
        n?.id !== payload?.meta?.workflowId
          ? n
          : {
              ...n,
              steps: n?.steps?.filter((m) => !payload?.meta?.ids.includes(m?.id)),
              graph: payload?.data?.find((n) => n.type === 'graph')?.data
            }
      )
    }),

    GET_APP_ACTION_OUTPUT_SUCCESS: (state, payload) => ({
      ...state,
      appActionOutputs: mergeByProp(['actionId', 'statusId'], [...state.appActionOutputs], [...payload?.data])
    }),

    GET_APP_ACTION_OUTBOUND_OUTPUT_SUCCESS: (state, payload) => ({
      ...state,
      appActionOutboundOutputs: mergeByProp('actionId', [...state.appActionOutboundOutputs], [...payload?.data])
    }),

    SET_ACTIVE_CANVAS_STEP_ADD_BUTTON_RELATIONSHIPS: (state, payload) => ({
      ...state,
      activeCanvasStepAddButtonRelationships: payload
    }),

    SET_STEP_STATE: (state, payload) => ({
      ...state,
      stepState: { ...state.stepState, ...payload }
    }),

    SET_STEP_OUTPUT: (state, payload) => ({
      ...state,
      stepOutput: {
        ...state.stepOutput,
        [payload.stepId]: {
          ...state.stepOutput[payload.stepId],
          data: payload.newData
        }
      }
    }),

    POST_RUN_WORKFLOW_SUCCESS: (state, payload) => ({
      ...state,
      workflowRunUpdate: {
        ...state.workflowRunUpdate,
        workflowId: payload?.data?.workflowId,
        workflowLogId: payload?.data?.workflowLogId,
        isRunning: true
      }
    }),

    GET_WORKFLOW_LOGS_SUCCESS: (state, payload) => ({
      ...state,
      workflowLogs: payload?.data
    }),

    GET_AUDIT_LOGS_SUCCESS: (state, payload) => ({
      ...state,
      auditLogs: { data: payload?.data, meta: payload?.meta || {} }
    }),

    GET_WORKFLOW_RUN_DETAILS_LOGS_SUCCESS: (state, payload) => ({
      ...state,
      workflowRunDetailsLogs: payload?.data
    }),

    GET_WORKFLOW_LOG_BY_ID_SUCCESS: (state, payload) => {
      const logData = payload?.data;

      const addToArray = (existingArray, newLog) => {
        if (!existingArray) return [newLog];
        const existingIndex = existingArray.findIndex((log) => log.id === newLog.id);
        if (existingIndex >= 0) {
          const updatedArray = [...existingArray];
          updatedArray[existingIndex] = newLog;
          return updatedArray;
        } else {
          return [newLog, ...existingArray];
        }
      };

      return {
        ...state,
        workflowLogs: addToArray(state.workflowLogs, logData),
        workflowRunDetailsLogs: addToArray(state.workflowRunDetailsLogs, logData)
      };
    },

    GET_WORKFLOW_LOGS_METADATA_SUCCESS: (state, payload) => ({
      ...state,
      workflowLogsMetadata: payload?.data
    }),

    GET_WORKFLOW_LOG_OUTPUT_SUCCESS: (state, payload) => ({
      ...state,
      workflowLogOutput: {
        ...state.workflowLogOutput,
        [payload?.meta?.logId]: payload?.data
      }
    }),

    GET_WORKFLOW_LOG_OPERATION_LOGS_SUCCESS: (state, payload) => ({
      ...state,
      workflowLogOperationLogs: {
        ...state.workflowLogOperationLogs,
        [payload?.meta?.logId]: payload?.data
      }
    }),

    GET_WORKFLOW_RESPONSES_SUCCESS: (state, payload) => ({
      ...state,
      stepOutput: {
        ...state.stepOutput,
        ...(payload?.data || {})
      }
    }),

    SET_WORKFLOW_LOOKUP: (state, payload) => ({
      ...state,
      workflowLookups: {
        ...state.workflowLookups,
        [payload.id]: { data: payload?.data, args: payload?.args, options: payload?.options }
      }
    }),

    GET_WORKFLOW_OPERATION_LOGS_SUCCESS: (state, payload) => ({
      ...state,
      workflowOperationLogs: mergeByProp('id', [...state.workflowOperationLogs], [...payload?.data])
    }),

    GET_WORKFLOW_OPERATION_LOG_OUTPUT_SUCCESS: (state, payload) => ({
      ...state,
      workflowOperationLogOutput: {
        ...state.workflowOperationLogOutput,
        [payload?.meta?.id]: payload?.data
      }
    }),

    DISMISS_WORKFLOW_OPERATION_LOGS_SUCCESS: (state, payload) => ({
      ...state,
      workflowOperationLogs: state?.workflowOperationLogs?.filter((n) => !payload?.meta?.ids.includes(n?.id))
    }),

    DUPLICATE_WORKFLOW_SUCCESS: (state, payload) => {
      if (payload?.meta?.companyId !== payload?.meta?.sourceCompanyId) {
        // This means we are duplicating from one company to another
        return state;
      }

      const workflow = payload.data?.[0];
      if (!workflow) {
        return state;
      }

      return {
        ...state,
        workflows: [...state.workflows, ...payload.data],
        workflowsMetadata: [...state.workflowsMetadata, { ...workflow.meta, id: workflow.id, version: workflow.version }]
      };
    },

    CREATE_FUNCTION_FROM_STEPS_SUCCESS: (state, payload) => {
      const workflow = payload.data?.[0];
      if (!workflow) {
        return state;
      }

      return {
        ...state,
        workflows: [...state.workflows, ...payload.data],
        workflowsMetadata: [...state.workflowsMetadata, { ...workflow.meta, id: workflow.id, version: workflow.version }]
      };
    },

    SET_WORKFLOW_RUN_UPDATE: (state, payload) => ({
      ...state,
      workflowRunUpdate: {
        ...state.workflowRunUpdate,
        workflowId: payload.workflowId,
        isRunning: payload.isRunning,
        isRunningSlice: payload.isRunningSlice,
        stepId: payload.stepId || undefined,
        operationLogs: payload?.operationLogs || state.workflowRunUpdate?.operationLogs || [],
        stepRunCount: payload?.stepRunCount || state.workflowRunUpdate?.stepRunCount || {},
        startedOn: payload.isRunning && !state.workflowRunUpdate?.isRunning ? new Date().toISOString() : state.workflowRunUpdate?.startedOn
      }
    }),

    CANCEL_WORKFLOW_SUCCESS: (state) => ({
      ...state,
      workflowRunUpdate: {
        ...state.workflowRunUpdate,
        isRunning: false,
        isCancelling: false,
        stepId: undefined,
        operationLogs: [],
        stepRunCount: {}
      }
    }),

    GET_PUB_SUB_MESSAGE_workflowRunUpdate: (state, payload) => {
      let updatedWorkflows = state.workflows;
      let updatedWorkflowsMetadata = state.workflowsMetadata;

      if (state.workflowRunUpdate?.isRunning === true && payload?.meta?.isRunning === false) {
        const currentDate = new Date().toISOString();
        const finishedOn = payload?.finishedOn || currentDate;
        const startedOn = payload?.startedOn || state.workflowRunUpdate?.startedOn || currentDate;

        updatedWorkflows = state.workflows.map((workflow) =>
          workflow.id === payload.workflowId
            ? {
                ...workflow,
                lastSuccessfulFinishedOn: finishedOn,
                lastSuccessfulStartedOn: startedOn
              }
            : workflow
        );

        if (state.workflowsMetadata?.length) {
          updatedWorkflowsMetadata = state.workflowsMetadata.map((metadata) =>
            metadata.id === payload.workflowId
              ? {
                  ...metadata,
                  lastSuccessfulFinishedOn: finishedOn,
                  lastSuccessfulStartedOn: startedOn
                }
              : metadata
          );
        }
      }

      return {
        ...state,
        workflows: updatedWorkflows,
        workflowsMetadata: updatedWorkflowsMetadata,
        workflowLogs: mergeByProp('id', state.workflowLogs, [payload], (old, nw) => {
          return { ...old, ...nw };
        }),
        workflowRunUpdate: {
          ...state.workflowRunUpdate,
          operationLogs: payload?.operationLog?.createdOn && !state.workflowRunUpdate?.operationLogs?.some((log) => log.createdOn === payload.operationLog.createdOn) ? [...(state.workflowRunUpdate?.operationLogs || []), payload.operationLog] : state.workflowRunUpdate?.operationLogs || [],
          stepRunCount: payload?.meta?.stepRunCount || state.workflowRunUpdate?.stepRunCount || {},
          isRunning: payload?.meta?.isRunning ?? state.workflowRunUpdate?.isRunning,
          isCancelling: payload?.meta?.isCancelling ?? state.workflowRunUpdate?.isCancelling,
          stepId: payload?.meta?.step?.id
        }
      };
    },

    GET_PUB_SUB_MESSAGE_memoryUpdated: (state, payload) => ({
      ...state,
      workflows: state?.workflows?.map((n) => (n?.id !== payload?.meta?.workflowId ? n : { ...n, variables: { ...n.variables, memory: payload.data } }))
    }),

    GET_WORKFLOW_SNAPSHOTS_SUCCESS: (state, payload) => ({
      ...state,
      workflowSnapshots: payload?.data
    }),

    GET_WORKFLOW_AUTOSAVES_SUCCESS: (state, payload) => ({
      ...state,
      workflowAutosaves: payload?.data
    }),

    GET_WORKFLOW_SNAPSHOT_SUCCESS: (state, payload) => ({
      ...state,
      workflowSnapshots: mergeByProp('id', state.workflowSnapshots, payload.data, (old, nw) => {
        return { ...old, ...nw };
      })
    }),

    PUT_WORKFLOW_SNAPSHOT_SUCCESS: (state, payload) => {
      const workflow = payload?.data?.find((n) => n.type === 'workflow')?.data;
      return {
        ...state,
        workflows: mergeByProp(['id', 'version'], state.workflows, [...workflow]),
        workflowSnapshots: payload?.data?.find((n) => n.type === 'snapshot')?.data
      };
    },

    RESTORE_WORKFLOW_AUTOSAVE_SUCCESS: (state, payload) => {
      const workflow = payload?.data?.find((n) => n.type === 'workflow')?.data;
      return {
        ...state,
        workflows: mergeByProp(['id', 'version'], state.workflows, [...workflow]),
        workflowSnapshots: payload?.data?.find((n) => n.type === 'snapshot')?.data
      };
    },

    GET_LATEST_WORKFLOW_LOGS_SUCCESS: (state, payload) => ({
      ...state,
      workflowListLogs: payload.data
    }),

    PUT_USER_WORKFLOW_ALERT_SUCCESS: (state, payload) => ({
      ...state,
      userWorkflowAlerts: payload.data
    }),

    POST_PARSE_IMPORT_SETTINGS_SUCCESS: (state, payload) => {
      return {
        ...state,
        actionSuggestions: {
          ...state.actionSuggestions,
          [payload.meta.appId]: {
            ...state.actionSuggestions[payload.meta.appId],
            [payload.meta.version]: {
              [payload.meta.actionId]: payload.data
            }
          },
          error: false
        }
      };
    },

    POST_PARSE_IMPORT_SETTINGS_FAIL: (state) => {
      return {
        ...state,
        actionSuggestions: {
          ...state.actionSuggestions,
          error: true
        }
      };
    },

    POST_PARSE_IMPORT_SETTINGS_RESET: (state) => {
      return {
        ...state,
        actionSuggestions: {
          ...state.actionSuggestions,
          error: false
        }
      };
    },

    PUBLISH_APP_SUCCESS: (state, payload) => ({
      ...state,
      appsMetadata: state?.appsMetadata?.map((app) =>
        app?.id === payload?.data?.appId
          ? {
              ...app,
              version: payload?.data?.version,
              publishedVersion: payload?.data?.publishedVersion,
              updatedOn: payload?.data?.updatedOn,
              updatedBy: payload?.data?.updatedBy
            }
          : app
      ),
      appsVersions: mergeByProp(['id', 'version'], [...state.appsVersions], [{ id: payload?.meta?.appId, version: payload?.meta?.appVersion }], (orig) => ({
        ...orig,
        updatedOn: new Date(),
        meta: {
          ...orig?.meta,
          changesSincePublish: 0
        }
      }))
    }),

    SET_WORKFLOW_MEMORY: (state, payload) => ({
      ...state,
      workflows: state?.workflows?.map((n) => (n?.id !== payload?.workflowId ? n : { ...n, variables: { ...n.variables, memory: { ...n.variables.memory, [payload.key]: payload.value } } }))
    }),

    GET_WORKFLOW_MEMORY_SUCCESS: (state, payload) => ({
      ...state,
      workflows: state?.workflows?.map((n) => (n?.id !== payload?.meta?.workflowId ? n : { ...n, variables: { ...n.variables, memory: payload.data } }))
    }),

    SET_WORKFLOW_MEMORY_SUCCESS: (state, payload) => ({
      ...state,
      workflows: state?.workflows?.map((n) => (n?.id !== payload?.meta?.workflowId ? n : { ...n, variables: { ...n.variables, memory: payload.data } }))
    }),

    DELETE_WORKFLOW_MEMORY_SUCCESS: (state, payload) => ({
      ...state,
      workflows: state?.workflows?.map((n) =>
        n?.id !== payload?.meta?.workflowId
          ? n
          : {
              ...n,
              variables: {
                ...n.variables,
                memory: payload.data
              }
            }
      )
    }),

    PUSH_CHANGES_QUEUE_ITEM: (state, payload) => ({
      ...state,
      changesQueue: mergeByProp('uniqueKey', state.changesQueue, [{ ...payload, uniqueKey: getUniqueKey(payload) }])
    }),

    FLUSH_CHANGES_QUEUE: (state) => ({
      ...state,
      changesQueue: []
    }),

    FLUSH_UPDATE_QUEUE: (state) => ({
      ...state,
      updateQueue: []
    }),

    GET_PUB_SUB_MESSAGE_syncBatchUpdate: (state, payload) => {
      const loadedAppIds = new Set(state.appsVersions?.map((a) => a.id) || []);
      const loadedWorkflowIds = new Set(state.workflows?.map((w) => w.id) || []);

      const appsToUpdate = payload.batch.filter((el) => el.type === 'app' && loadedAppIds.has(el.appId));
      const workflowStepsToUpdate = payload.batch.filter((el) => el.type === 'workflowStep' && loadedWorkflowIds.has(el.workflowId));
      const workflowsToUpdate = payload.batch.filter((el) => el.type === 'workflow' && loadedWorkflowIds.has(el.workflowId));
      const credentialsToUpdate = payload.batch.filter((el) => el.type === 'credential');
      const platformSecretsToUpdate = payload.batch.filter((el) => el.type === 'platformSecret');
      const mockTypesToUpdate = payload.batch.filter((el) => el.type === 'mockType');
      const projectsToUpdate = payload.batch.filter((el) => el.type === 'project');
      const projectIssuesToUpdate = payload.batch.filter((el) => el.type === 'projectIssue');

      const allItems = [...appsToUpdate, ...workflowStepsToUpdate, ...workflowsToUpdate, ...credentialsToUpdate, ...platformSecretsToUpdate, ...mockTypesToUpdate, ...projectsToUpdate, ...projectIssuesToUpdate];
      if (allItems.length === 0) {
        return state;
      }

      let newUpdateQueue = mergeByProp(['appId', 'appVersion'], [...state.updateQueue], [...appsToUpdate]);
      newUpdateQueue = mergeByProp(['workflowId'], [...newUpdateQueue], [...workflowsToUpdate]);
      newUpdateQueue = mergeByProp(['workflowId', 'stepId'], [...newUpdateQueue], [...workflowStepsToUpdate]);
      newUpdateQueue = mergeByProp(['credentialId'], [...newUpdateQueue], [...credentialsToUpdate]);
      newUpdateQueue = mergeByProp(['platformSecretId'], [...newUpdateQueue], [...platformSecretsToUpdate]);
      newUpdateQueue = mergeByProp(['projectId'], [...newUpdateQueue], [...projectsToUpdate]);
      newUpdateQueue = mergeByProp(['issueId'], [...newUpdateQueue], [...projectIssuesToUpdate]);
      newUpdateQueue = mergeByProp(['mockTypeId'], [...newUpdateQueue], [...mockTypesToUpdate]);
      return { ...state, updateQueue: newUpdateQueue };
    },

    SET_IS_SYNCING: (state, payload) => ({
      ...state,
      isSyncing: payload
    }),

    SET_IS_UPDATING: (state, payload) => ({
      ...state,
      isUpdating: payload
    }),

    PUT_SYNC_BATCH_SUCCESS: (state, payload) => {
      const { apps = [], workflowSteps = [], workflows = [], credentials = [], platformSecrets = [], failed = [], companys = [], projects = [], projectIssues = [] } = payload;

      const appsToUpdate = apps //
        .map((n) => ({ type: 'app', appId: n?.id, appVersion: n?.version }))
        .filter((n) => !!n?.appId && !!n?.appVersion);

      const workflowStepsToUpdate = workflowSteps //
        .map((n) => ({ type: 'workflowStep', workflowId: n?.workflowId, stepId: n?.id, companyId: n?.companyId, userId: n?.userId }))
        .filter((n) => !!n?.workflowId && !!n?.stepId);

      const workflowsToUpdate = workflows //
        .map((n) => ({ type: 'workflow', workflowId: n?.id, companyId: n?.companyId }))
        .filter((n) => !!n?.workflowId);

      const credentialsToUpdate = credentials //
        .map((n) => ({ type: 'credential', credentialId: n?.credentialId, companyId: n?.companyId, appId: n?.appId }))
        .filter((n) => !!n?.credentialId && !!n?.companyId && !!n?.appId);

      const platformSecretsToUpdate = platformSecrets //
        .map((n) => ({ type: 'platformSecret', platformSecretId: n?.id, companyId: n?.companyId, appId: n?.appId }))
        .filter((n) => !!n?.platformSecretId && !!n?.companyId && !!n?.appId);

      const companiesToUpdate = companys //
        .map((n) => ({ type: 'company', companyId: n?.id }))
        .filter((n) => !!n?.companyId);
      const projectsToUpdate = projects //
        .map((n) => ({ type: 'project', projectId: n?.id || n?.projectId, companyId: n?.companyId }))
        .filter((n) => !!n?.projectId && !!n?.companyId);
      const projectIssuesToUpdate = projectIssues //
        .map((n) => ({ type: 'projectIssue', issueId: n?.id || n?.issueId, projectId: n?.projectId, companyId: n?.companyId }))
        .filter((n) => !!n?.issueId && !!n?.companyId);

      let newUpdateQueue = mergeByProp(['appId', 'appVersion'], [...state.updateQueue], [...appsToUpdate]);
      newUpdateQueue = mergeByProp(['workflowId'], [...newUpdateQueue], [...workflowsToUpdate]);
      newUpdateQueue = mergeByProp(['workflowId', 'stepId'], [...newUpdateQueue], [...workflowStepsToUpdate]);
      newUpdateQueue = mergeByProp(['credentialId'], [...newUpdateQueue], [...credentialsToUpdate]);
      newUpdateQueue = mergeByProp(['platformSecretId'], [...newUpdateQueue], [...platformSecretsToUpdate]);
      newUpdateQueue = mergeByProp(['companyId'], [...newUpdateQueue], [...companiesToUpdate]);
      newUpdateQueue = mergeByProp(['projectId'], [...newUpdateQueue], [...projectsToUpdate]);
      newUpdateQueue = mergeByProp(['issueId'], [...newUpdateQueue], [...projectIssuesToUpdate]);

      return { ...state, updateQueue: newUpdateQueue, syncFailed: failed?.length > 0 };
    },

    SET_SYNC_FAILED: (state, payload) => ({
      ...state,
      syncFailed: payload
    }),

    SET_LAST_SYNC_RESULT: (state, payload) => ({
      ...state,
      lastSyncResult: payload
    }),

    GET_WORKFLOW_TAGS_SUCCESS: (state, payload) => ({
      ...state,
      workflowTags: payload.data
    }),

    POST_WORKFLOW_TAG_SUCCESS: (state, payload) => ({
      ...state,
      workflowTags: mergeByProp('id', [...state.workflowTags], [...payload.data])
    }),

    POST_WORKFLOW_TAG: (state, payload) => ({
      ...state,
      workflowTags: mergeByProp('id', [...state.workflowTags], [payload])
    }),

    PUT_WORKFLOW_TAG_SUCCESS: (state, payload) => ({
      ...state,
      workflowTags: mergeByProp('id', [...state.workflowTags], [...payload.data])
    }),

    PUT_WORKFLOW_TAG: (state, payload) => ({
      ...state,
      workflowTags: mergeByProp('id', [...state.workflowTags], [payload])
    }),

    DELETE_WORKFLOW_TAG_SUCCESS: (state, payload) => ({
      ...state,
      workflowTags: state.workflowTags.filter((tag) => tag.id !== payload.meta?.tagId)
    }),

    DELETE_WORKFLOW_TAG: (state, payload) => ({
      ...state,
      workflowTags: state.workflowTags.filter((tag) => tag.id !== payload.meta?.tagId)
    }),

    GET_USER_STATE_SUCCESS: (state, payload) => ({
      ...state,
      appsMetadata: payload.data?.appsMetadata,
      recipes: payload?.data?.recipes
    }),

    TRIGGER_WORKFLOW_UPDATE: (state, payload) => {
      const stepAppId = payload?.stepAppId;
      const workflowId = payload?.workflowId;
      const stepId = payload?.stepId;
      const actionId = state?.workflows //
        ?.find((n) => n?.id === workflowId)
        ?.steps?.find((n) => n?.id === stepId)?.actionId;
      const uniqueKey = `${workflowId}-${stepAppId}-${stepId}-${actionId}`;

      return {
        ...state,
        triggerMutationsQueue: mergeByProp('uniqueKey', state.triggerMutationsQueue, [{ ...payload, actionId, uniqueKey, sortableKey: Date.now(), type: 'update' }])
      };
    },

    CLEANUP_TRIGGERS_QUEUE: (state, payload) => {
      const { actionId, stepId } = payload;
      const uniqueKey = `${stepId}-${actionId}`;

      return {
        ...state,
        triggerMutationsQueue: mergeByProp('uniqueKey', state.triggerMutationsQueue, [{ ...payload, uniqueKey, sortableKey: Date.now(), type: 'cleanup' }])
      };
    },

    TRIGGER_MUTATIONS_DONE: (state, payload) => ({
      ...state,
      triggerMutationsQueue: state.triggerMutationsQueue.filter((n) => n.uniqueKey !== payload.uniqueKey)
    }),

    PUSH_RECENTLY_USED_APP: (state, appId) => ({
      ...state,
      recentlyUsedApps: {
        ...state.recentlyUsedApps,
        [appId]: new Date().getTime()
      }
    }),

    SET_NEXT_STEPS_STATE: (state, payload) => ({
      ...state,
      nextStepsState: payload
    }),

    SET_NEXT_STEP_SINGLE_STATE: (state, payload) => ({
      ...state,
      nextStepsState: state?.nextStepsState?.map((step, i) => (i === payload.index ? payload.progress : step))
    }),

    SET_DEPENDENT_STEP_IDS: (state, payload) => ({
      ...state,
      dependentStepIds: payload
    }),

    GET_WORKFLOW_STEP_COMMENTS_SUCCESS: (state, payload) => ({
      ...state,
      workflowComments: {
        ...state.workflowComments,
        [payload.meta.workflowId]: payload.data
      }
    }),

    POST_WORKFLOW_STEP_COMMENT_SUCCESS: (state, payload) => ({
      ...state,
      workflowComments: {
        ...state.workflowComments,
        [payload.meta.workflowId]: [...state.workflowComments[payload.meta.workflowId], ...payload.data]
      }
    }),

    PUT_WORKFLOW_STEP_COMMENT_SUCCESS: (state, payload) => ({
      ...state,
      workflowComments: {
        ...state.workflowComments,
        [payload.meta.workflowId]: state.workflowComments[payload.meta.workflowId].map((n) => (n.id === payload.meta.commentId ? payload.data : n))
      }
    }),

    DELETE_WORKFLOW_STEP_COMMENT_SUCCESS: (state, payload) => ({
      ...state,
      workflowComments: {
        ...state.workflowComments,
        [payload.meta.workflowId]: state.workflowComments[payload.meta.workflowId].filter((n) => n.id !== payload.meta.commentId)
      }
    }),

    GET_WORKFLOW_STEP_WEBHOOK_RESPONSES_SUCCESS: (state, payload) => ({
      ...state,
      webhookResponses: {
        ...state.webhookResponses,
        [payload.meta.stepId]: payload.data
      },
      stepOutput: {
        ...state.stepOutput,
        [payload.meta.stepId]: payload?.data?.[0]
      }
    }),

    GET_WORKFLOW_STEP_LONG_POLL_RESPONSES_SUCCESS: (state, payload) => {
      return {
        ...state,
        longPollResponses: {
          ...state.longPollResponses,
          [payload.meta.stepId]: payload.data
        }
      };
    },

    SET_LONG_POLL_RESPONSE: (state, payload) => ({
      ...state,
      longPollResponses: {
        ...state.longPollResponses,
        [payload.stepId]: payload.data
      }
    }),

    SET_ACTIVE_STATUS_ID: (state, payload) => ({
      ...state,
      activeStatusId: payload
    }),

    SET_TUTORIAL_SCRIPT: (state, payload) => ({
      ...state,
      tutorialScripts: {
        ...state.tutorialScripts,
        [payload?.slug]: payload?.script
      }
    }),

    SET_TUTORIAL_REQUIREMENT: (state, payload) => ({
      ...state,
      currentTutorialRequirement: payload || null
    }),

    SET_TUTORIAL_EVENT: (state, payload) => ({
      ...state,
      tutorialEvent: payload
    }),

    CLEAR_TUTORIAL_EVENT: (state) => ({
      ...state,
      tutorialEvent: null
    }),

    TRIGGER_DEPENDENT_LOOKUP_RELOAD: (state, payload) => {
      if (!payload || !payload.id) return state;
      return {
        ...state,
        lookupReloadQueue: state.lookupReloadQueue.includes(payload.id) ? state.lookupReloadQueue : [...state.lookupReloadQueue, payload.id]
      };
    },

    REMOVE_LOOKUP_RELOAD_QUEUE: (state, payload) => {
      if (!payload || !payload.id) return state;
      return {
        ...state,
        lookupReloadQueue: state.lookupReloadQueue.filter((item) => item !== payload.id)
      };
    },

    GET_NETSUITE_RECORD_SCHEMA_SUCCESS: (state, payload) => {
      const error = payload?.data?.error;
      const roleId = payload?.data?.roleId;
      if (error) {
        return {
          ...state,
          netsuiteRecordSchema: {
            ...state.netsuiteRecordSchema,
            [payload.meta.recordType]: []
          },
          netsuiteRecordSchemaError: {
            ...state.netsuiteRecordSchemaError,
            [`${payload.meta.recordType}-${payload.meta.realmId}`]: { error, roleId }
          }
        };
      }
      return {
        ...state,
        netsuiteRecordSchema: {
          ...state.netsuiteRecordSchema,
          [payload.meta.recordType]: payload?.data?.[0] || payload?.data
        },
        netsuiteRecordSchemaError: {
          ...state.netsuiteRecordSchemaError,
          [`${payload.meta.recordType}-${payload.meta.realmId}`]: null
        }
      };
    },

    SEARCH_CREDENTIALS_SUCCESS: (state, payload) => ({
      ...state,
      searchCredentials: payload.data
    }),

    SET_GRAPHQL_SCHEMA: (state, payload) => {
      const { credentialId, queryAction, schema } = payload;
      const schemaKey = `${credentialId}-${queryAction}`;
      return {
        ...state,
        graphqlSchema: {
          ...state.graphqlSchema,
          [schemaKey]: {
            schema,
            timestamp: new Date().getTime()
          }
        }
      };
    },

    SET_MIND_CLOUD_EMBEDDED_SLACK_INSTALLATION: (state, payload) => ({
      ...state,
      slackInstallation: payload.data
    }),

    SET_MIND_CLOUD_EMBEDDED_SLACK_INSTALLATION_CHANNELS: (state, payload) => ({
      ...state,
      slackInstallationChannels: payload.data
    }),

    SET_NETSUITE_CACHE: (state, payload) => ({
      ...state,
      netsuiteCache: { ...state.netsuiteCache, [payload.key]: payload.data }
    }),

    UPDATE_TEST_INSTALLATION_METADATA: (state, payload) => ({
      ...state,
      workflows: state.workflows.map((workflow) => (workflow.id === payload.workflowId && workflow.version === 'draft' ? { ...workflow, installationMetadata: payload.metadata } : workflow))
    })
  },
  persistConfig
);
